import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PriceCalculationStickyProps } from './PriceCalculationSticky.types';
import styles from './PriceCalculationSticky.module.scss';
import { Container } from 'react-bootstrap';
import FalconButtonV2 from '../FalconButtonV2/FalconButtonV2';
import { countZero, replacePlaceholder} from '../../utils/utils';

const PriceCalculationSticky: React.FC<PriceCalculationStickyProps> = ({
  totalOnetimeAmount = 0,
  totalRecurring=0,
  onContinue,
  cartData,
  content,
  flow
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const stickyContainerRef = useRef<HTMLDivElement>(null);
  // Use controlled state if provided, otherwise use internal state
  const isButtonDisabled = flow ? true : false;
  const isExpanded =internalExpanded;
  // Memoized handlers
  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent toggle when clicking the button
      onContinue?.();
    },
    [onContinue]
  );

  const handleCollapse = useCallback(() => {
    setInternalExpanded(false);
  }, []);

  // Handle click outside to collapse
  useEffect(() => {
    if (!internalExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        stickyContainerRef.current &&
        !stickyContainerRef.current.contains(event.target as Node)
      ) {
        setInternalExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, handleCollapse]);

  const getContractDuration = (contractDuration:string) => {
    return contractDuration && contractDuration !== '0' ? replacePlaceholder(content?.contractDuration, [contractDuration]) : content?.noMinContractDuration;
  };

  const productsNamesList = cartData?.map(
    (item:any) =>
      item?.itemName || ''
  )?.join(', ');
  return (
    <div className={styles.stickyContainer} ref={stickyContainerRef}>
      {/* Collapsed View - Always Visible */}
      <Container>
        <div
          className={styles.collapsedView}
          onClick={() => setInternalExpanded(!internalExpanded)}
        >
          <div className={styles.priceInfo}>
            <div className={styles.priceWrapper}>
              {totalRecurring !== 0 && (
             <div className={styles.price}>
                {countZero(totalRecurring)}
                <span className={styles.period}>{content?.monthlyText}</span>
              </div>
             
            )}
            {totalOnetimeAmount !== 0 && (
              <div className={styles.totalSection}><div>{content?.oneTime || ''}&nbsp; 
              </div>
                  <div className={styles.price}>
                    {content?.recurringText} {countZero(totalOnetimeAmount)}
                  </div></div>
                )}
            </div>
            {/* {productsName && <div className={styles.productName}>{productsName}</div>} */}
            {productsNamesList && <div className={styles.productName}>{productsNamesList}</div>}
          </div>
          <FalconButtonV2
            content={content?.continueBtn?.[0]}
            eventAction='text'
            metadata={{}}
            disabled={isButtonDisabled}
            aria-disabled={isButtonDisabled}
            onClick={handleButtonClick}
          />
        </div>

        {/* Expanded View - Toggleable Content */}
        {internalExpanded && (
          <div className={styles.expandedView}>
            <hr />

            <div className={styles.productsList}>
              {content?.title && <h6 className={styles.pricingOverview}>{content?.title}</h6>}
              {cartData?.map((product:any, index:Number) => {
                const contractDuration =product?.itemType === 'MOBILEPOSTPAID_RATEPLAN' ? getContractDuration(product?.contractDuration || product?.contractTerm) : '';
                return (
                  <div key={product.productName || index} className={styles.productItem}>
                    <div className={styles.productItemInfo}>
                      {product?.itemName && (
                        <div className={styles.productItemName}>{product?.itemName}</div>
                      )}
                      {product?.itemType === 'HOME_SECURITY' && (
                        <div className={styles.hardwarePayment}>
                          {content?.hsDevicesLabel && <span>{content?.hsDevicesLabel}</span>}{' '}
                          {product?.accessoryCount > 0 &&
                            content?.hsAdditionalAccLabel &&
                            replacePlaceholder(content?.hsAdditionalAccLabel, [
                              product?.accessoryCount
                            ])}
                        </div>
                      )}
                    </div>
                    <div className={styles.productItemPriceWrapper}>
                      {product?.itemOneTimePrice > 0 ? 
                       <div className={styles.productItemPrice}>
                          <div className={styles.derivedPrice}>
                            {countZero(product?.itemOneTimePrice)}
                            <span>/-</span>
                          </div>
                        </div> : product?.itemDerivedPrice ? (
                        <div className={styles.productItemPrice}>
                          <div className={styles.derivedPrice}>
                            {countZero(product?.itemDerivedPrice)}
                            <span>{content?.monthlyText}</span>
                          </div>
                          <span className={styles.recurringPrice}>{countZero(product?.itemDiscountedPrice)}</span>
                        </div>
                      ) : (
                        <div className={styles.productItemPrice}>
                          <div className={styles.derivedPrice}>
                            {countZero(product?.itemDiscountedPrice)}
                            <span>{content?.monthlyText}</span>
                          </div>
                        </div>
                      )}
                      {contractDuration && (
                        <div className={styles.productItemDuration}>{contractDuration}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default PriceCalculationSticky;
